import { WORKFLOW_SORT_FIELDS, WORKFLOW_SUBJECTS, type WorkflowSubject } from '@sahab/contracts'
import { z } from 'zod'

type WorkflowSortField = (typeof WORKFLOW_SORT_FIELDS)[number]
export type WorkflowSort = WorkflowSortField | `-${WorkflowSortField}`

export const WORKFLOW_PAGE_SIZES = [25, 50, 100] as const

export function isWorkflowSort(value: string): value is WorkflowSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (WORKFLOW_SORT_FIELDS as readonly string[]).includes(field)
}

/** The list's state filter: Active (routing new submissions), Disabled, Draft only (never published). */
export const WORKFLOW_STATES = ['active', 'disabled', 'unpublished'] as const
export type WorkflowStateFilter = (typeof WORKFLOW_STATES)[number]

/** The workflow list lives in the URL; invalid values fall back to the defaults. */
export const workflowSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof WORKFLOW_PAGE_SIZES)[number] =>
      (WORKFLOW_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  sort: z.string().refine(isWorkflowSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  state: z.enum(WORKFLOW_STATES).optional().catch(undefined),
  subject: z.enum(WORKFLOW_SUBJECTS).optional().catch(undefined),
})
export type WorkflowSearch = z.infer<typeof workflowSearchSchema>

export interface WorkflowListParams {
  page: number
  pageSize: number
  sort: WorkflowSort
  q: string | undefined
  state: WorkflowStateFilter | undefined
  subject: WorkflowSubject | undefined
}

export function toListQuery(search: WorkflowSearch): WorkflowListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? WORKFLOW_PAGE_SIZES[0],
    // Routing order by default: the order in which workflows are tried.
    sort: (search.sort as WorkflowSort | undefined) ?? 'priority',
    q: search.q || undefined,
    state: search.state,
    subject: search.subject,
  }
}

export const WORKFLOW_TABS = ['overview', 'builder', 'runs', 'versions', 'audit'] as const
export type WorkflowTab = (typeof WORKFLOW_TABS)[number]

/** The detail page: the open tab and whether the test panel is open. */
export const workflowDetailSearchSchema = z.object({
  tab: z.enum(WORKFLOW_TABS).optional().catch(undefined),
  test: z.boolean().optional().catch(undefined),
})
export type WorkflowDetailSearch = z.infer<typeof workflowDetailSearchSchema>
